import { inject, Injectable } from '@angular/core'

import { MessageService } from 'primeng/api'
import { Subject } from 'rxjs'

import { Role, TI_CUSTOM_ROLE_ID_RANGE } from '@ti/contracts'
import { ApiService } from '@ti/core/api'
import { DIALOG_FACADE } from '@ti/core/contracts/dialog'
import { LANGUAGE_SERVICE } from '@ti/core/contracts/i18n'

@Injectable({ providedIn: 'root' })
export class RolesManager {
  public readonly events$ = {
    updated: new Subject<number[] | void>(),
  }

  protected readonly api = inject(ApiService)
  protected readonly dialogFacade = inject(DIALOG_FACADE)
  protected readonly languageService = inject(LANGUAGE_SERVICE)
  protected readonly messageService = inject(MessageService)

  public constructor() {}

  public async create(data: Partial<Role>) {
    try {
      const role = await this.api.role.create(data)
      this.events$.updated.next([role.id])

      this.messageService.add({
        severity: 'success',
        detail: await this.languageService.translate('roles.messages.add-success'),
      })
      return role
    } catch (e) {
      console.error(`Cannot create role`, e)
    }
  }

  public async update(id: number, updates: Partial<Role>) {
    try {
      const role = await this.api.role.update(id, updates)
      this.events$.updated.next([role.id])

      this.messageService.add({
        severity: 'success',
        detail: await this.languageService.translate('roles.messages.edit-success', {
          name: updates.name,
        }),
      })
      return role
    } catch (e) {
      console.error(`Cannot update role`, e)
    }
  }

  public async duplicate(role: Role): Promise<Role | undefined> {
    try {
      // Override role name to ensure proper translation is used for default ones
      const newRole = await this.api.role.duplicate(role.id, { name: role.name })
      this.events$.updated.next()

      this.messageService.add({
        severity: 'success',
        detail: await this.languageService.translate('roles.messages.duplicate-success', {
          name: role.name,
        }),
      })
      return newRole
    } catch (e) {
      console.error(`Cannot duplicate role`, e)
    }
  }

  public async delete(role: Role) {
    const confirmed = await this.confirmDelete(role)
    if (!confirmed) return

    try {
      await this.api.role.remove(role.id)
      this.events$.updated.next()

      this.messageService.add({
        severity: 'success',
        detail: await this.languageService.translate('roles.messages.delete-success', {
          name: role.name,
        }),
      })
    } catch (e) {
      console.error(`Cannot create role`, e)
    }
  }

  public isCustom(roleId: number) {
    return roleId >= TI_CUSTOM_ROLE_ID_RANGE[0]
  }

  protected async confirmDelete(role: Role) {
    const dialogLabels = await this.languageService.massTranslate(
      {
        summary: 'roles.dialogs.delete.summary',
        description: 'roles.dialogs.delete.description',
        cancelButton: 'button.cancel',
        confirmButton: 'common.yes-delete',
      },
      { name: role.name },
    )

    return this.dialogFacade.confirm({
      ...dialogLabels,
      confirmButtonColor: 'var(--color-alert-500)',
      confirmButtonIcon: 'pi-trash',
      equalControlsWidth: true,
    })
  }
}
